/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import i18n from 'i18next';
import type { UseTranslationResponse } from 'react-i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { CORE_NAMESPACE, coreTranslations } from '@opengeoweb/core';
import {
  LAYER_SELECT_NAMESPACE,
  layerSelectTranslations,
} from '@opengeoweb/layer-select';
import { SHARED_NAMESPACE, sharedTranslations } from '@opengeoweb/shared';
import {
  TIME_SLIDER_NAMESPACE,
  timesliderTranslations,
} from '@opengeoweb/time-slider';
import {
  WEBMAP_NAMESPACE,
  WEBMAP_REACT_NAMESPACE,
  webmapReactTranslations,
  webmapTranslations,
} from '@opengeoweb/webmap-react';
import flySafeTranslations from '../locales/flysafe.json';

export const FLYSAFE_NAMESPACE = 'flysafe';

const allTranslations = [
  { ns: FLYSAFE_NAMESPACE, translations: flySafeTranslations },
  { ns: CORE_NAMESPACE, translations: coreTranslations },
  { ns: WEBMAP_REACT_NAMESPACE, translations: webmapReactTranslations },
  { ns: WEBMAP_NAMESPACE, translations: webmapTranslations },
  { ns: SHARED_NAMESPACE, translations: sharedTranslations },
  { ns: TIME_SLIDER_NAMESPACE, translations: timesliderTranslations },
  { ns: LAYER_SELECT_NAMESPACE, translations: layerSelectTranslations },
];

const languages = ['en', 'nl'] as const;

export const initFlySafeI18n = (): void => {
  if (!i18n.isInitialized && !i18n.isInitializing) {
    void i18n.use(initReactI18next).init({
      lng: 'en',
      defaultNS: FLYSAFE_NAMESPACE,
      ns: allTranslations.map(({ ns }) => ns),
      interpolation: { escapeValue: false },
      resources: {},
    });
  }

  for (const { ns, translations } of allTranslations) {
    for (const lang of languages) {
      i18n.addResourceBundle(
        lang,
        ns,
        (translations as Record<string, Record<string, string>>)[lang],
      );
    }
  }
};

const ns = [FLYSAFE_NAMESPACE];

export const translateKeyOutsideComponents = (
  key: string,
  params: Record<string, string | number> | undefined = undefined,
): string => i18n.t(key, { ns, ...params });

export const useFlySafeTranslation = (): UseTranslationResponse<
  typeof FLYSAFE_NAMESPACE,
  typeof i18n
> => useTranslation(ns);

export { i18n };
